import { library } from '@fortawesome/fontawesome-svg-core';
import { faCaretDown, faCaretUp, faEye, faUnlink, faUpload } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TranslationKey, useTranslator } from '@Localization';
import { Button } from '@mui/joy';
import { observer } from 'mobx-react-lite';
import { Dispatch, ReactElement, ReactNode, SetStateAction, SyntheticEvent, useState } from 'react';
import { S_Dropzone } from '../../../../design/dropzone/S_Dropzone';
import { S_Checkbox } from '../../../../design/inputs/checkbox/S_Checkbox';
import { S_ClipLoader } from '../../../../design/loaders/clipLoader/S_ClipLoader';
import { ColumnLayout } from '../../../../modules/common/layouts/columnLayout/ColumnLayout';
import { UnregisteredUsersSummaryPane } from '../../../../modules/projects/details/images/importAnnotations/ui/steps/summary/unregisteredUsersSummaryPane/UnregisteredUsersSummaryPane';
import { WizardModalDropZoneContent } from '../../../components/wizardModal/contents/WizardModalDropZoneContent';
import { EnableFor } from '../../../containers/EnableForRole';
import { ImportStage, IProjectImportReport } from '../projects.model';
import { useProjectPermissions } from '../projects.permissions';
import { ImportAnnotationsSummary } from './ImportAnnotationsSummary';
import { ImportAnnotationToolsSummary } from './ImportAnnotationToolsSummary';
import { ImportAttributesSummary } from './ImportAttributesSummary';
import { ImportImageSetsSummary } from './ImportImageSetsSummary';
import { ImportModalLayout } from './ImportModalLayout';
import { ImportModalOverlay } from './ImportModalOverlay';
import { ImportQuestionsSummary } from './ImportQuestionsSummary';

library.add(faUnlink, faEye, faCaretDown, faCaretUp, faUpload);

interface IProps {
  modalsShown(): void;
  modalHidden: () => Promise<void>;
  warningAccepted: () => void;
  filePicked: () => void;
  uploadStarted: () => Promise<void>;
  filesDropped: (files: File[]) => void;
  importAnnotationsChanged: () => void;
  importImageSetsChanged: () => void;
  goToProject: () => void;
  stage: ImportStage;
  canUpload: boolean;
  isShown: boolean;
  importAnnotations: boolean;
  importImageSets: boolean;
  canCloseModal: boolean;
  validationError?: TranslationKey;
  report?: IProjectImportReport;
  errorCodes?: string[];
  fileName?: string;
}

interface IImportModalContent {
  headerText: ReactNode;
  body: ReactNode;
  footer: ReactNode;
}

export const ImportModal = observer((props: IProps): ReactElement => {
  const { t } = useTranslator();
  const projectsPermissions = useProjectPermissions();
  const [isImageSetsPanelOpen, setIsImageSetsPanelOpen] = useState<boolean>(false);
  const [isToolsPanelOpen, setIsToolsPanelOpen] = useState<boolean>(false);
  const [isAttributesPanelOpen, setIsAttributesPanelOpen] = useState<boolean>(false);
  const [isQuestionsPanelOpen, setIsQuestionsPanelOpen] = useState<boolean>(false);

  const handlePanelToggle = (setPanelState: Dispatch<SetStateAction<boolean>>) => {
    return (): void => {
      setPanelState((prevState) => !prevState);
    };
  };

  const stepOneBody = (): IImportModalContent => ({
    headerText: `${t('import_settings')} - ${t('information')}`,
    body: (
      <>
        <p>{t('you_are_about_to_start_import_process')}</p>
        <p>{t('import_note')}</p>
        <p>{t('import_users_note')}</p>
        <div>{t('do_you_want_to_continue')}</div>
      </>
    ),
    footer: (
      <>
        <Button
          color='neutral'
          variant='outlined'
          onClick={() => {
            void props.modalHidden();
          }}
        >
          {t('cancel')}
        </Button>
        <Button onClick={props.warningAccepted}>{t('next')}</Button>
      </>
    ),
  });

  const stepTwoBody = (): IImportModalContent => ({
    headerText: `${t('import_settings')} - ${t('select_file')}`,
    body: (
      <WizardModalDropZoneContent
        fileName={props.fileName}
        filesDropped={props.filesDropped}
        fileValidationError={props.validationError}
      />
    ),
    footer: (
      <>
        <Button
          color='neutral'
          variant='outlined'
          onClick={() => {
            void props.modalHidden();
          }}
        >
          {t('cancel')}
        </Button>
        <Button
          disabled={!props.canUpload}
          onClick={props.filePicked}
        >
          {t('next')}
        </Button>
      </>
    ),
  });

  const stepThreeBody = (): IImportModalContent => ({
    headerText: `${t('import_settings')} - ${t('configure_import')}`,
    body: (
      <>
        <div>{t('pick_which_elements_should_be_imported')}:</div>
        <ColumnLayout size='sm'>
          <S_Checkbox
            id='tools-checkbox'
            checked={true}
            disabled={true}
            label={t('annotation_tools_and_questions')}
          />
          <S_Checkbox
            id='annotations-checkbox'
            checked={props.importAnnotations}
            onChange={props.importAnnotationsChanged}
            label={t('annotations')}
          />
          <S_Checkbox
            id='image-set-checkbox'
            checked={props.importImageSets}
            onChange={props.importImageSetsChanged}
            label={t('image_set_configuration')}
          />
        </ColumnLayout>
      </>
    ),
    footer: (
      <>
        <Button
          color='neutral'
          variant='outlined'
          onClick={() => {
            void props.modalHidden();
          }}
        >
          {t('cancel')}
        </Button>
        <Button
          onClick={() => {
            void props.uploadStarted();
          }}
        >
          {t('finish')}
        </Button>
      </>
    ),
  });

  const processingBody = (): IImportModalContent => ({
    headerText: `${t('import_settings')} - ${t('processing')}`,
    body: (
      <>
        <div>
          <p>{t('we_are_processing_your_file_please_wait')}</p>
        </div>
        <S_ClipLoader />
      </>
    ),
    footer: <></>,
  });

  const finishedBody = (): IImportModalContent => {
    const importedImageSetsCount = props.report?.importedImageSetsCount || 0;
    const importedToolsCount = props.report?.importedToolsCount || 0;
    const importedAttributesCount = props.report?.importedAttributesCount || 0;
    const importedQuestionsCount = props.report?.importedQuestionsCount || 0;

    return {
      headerText: `${t('import_settings')} - ${t('finished')}`,
      body: (
        <>
          <div>
            <p>{t('your_settings_have_been_successfully_imported')}</p>
            {props.report && (
              <ColumnLayout
                size='md'
                className='import-summary'
              >
                <ColumnLayout size='sm'>
                  <span className='import-summary-header'>{t('summary')}:</span>

                  <ImportAnnotationToolsSummary
                    isPanelOpen={isToolsPanelOpen}
                    handlePanelToggle={handlePanelToggle(setIsToolsPanelOpen)}
                    importedToolsCount={importedToolsCount}
                    rejectedTools={props.report.rejectedTools}
                    skippedToolsCount={props.report.skippedToolsCount}
                  />

                  <ImportAttributesSummary
                    isPanelOpen={isAttributesPanelOpen}
                    handlePanelToggle={handlePanelToggle(setIsAttributesPanelOpen)}
                    importedAttributesCount={importedAttributesCount}
                    rejectedAttributes={props.report.rejectedAttributes}
                    skippedAttributesCount={props.report.skippedAttributesCount}
                  />

                  <ImportQuestionsSummary
                    isPanelOpen={isQuestionsPanelOpen}
                    handlePanelToggle={handlePanelToggle(setIsQuestionsPanelOpen)}
                    importedQuestionsCount={importedQuestionsCount}
                    rejectedQuestions={props.report.rejectedQuestions}
                    skippedQuestionsCount={props.report.skippedQuestionsCount}
                  />

                  {props.importAnnotations && <ImportAnnotationsSummary {...props.report} />}

                  <ImportImageSetsSummary
                    isPanelOpen={isImageSetsPanelOpen}
                    handlePanelToggle={handlePanelToggle(setIsImageSetsPanelOpen)}
                    importedImageSetsCount={importedImageSetsCount}
                    rejectedImageSets={props.report.rejectedImageSets}
                    skippedImageSetsCount={props.report.skippedImageSetsCount}
                  />
                </ColumnLayout>
                <ColumnLayout size='sm'>
                  {props.report.assignedDatasetNames.length > 0 && (
                    <>
                      <span className='import-summary-header'>{t('datasets_from_file', { count: props.report.assignedDatasetNames.length })}:</span>
                      <ColumnLayout size='sm'>
                        {props.report.assignedDatasetNames.map((x) => (
                          <div key={x}>
                            {x} - {t('successfully_matched')}
                          </div>
                        ))}
                        {props.report.rejectedDatasetNames.map((x) => (
                          <div key={x}>
                            {x} - {t('not_found_in_the_current_workspace')}
                          </div>
                        ))}
                      </ColumnLayout>
                    </>
                  )}
                </ColumnLayout>
                {props.report.unregisteredUsers.length > 0 && <UnregisteredUsersSummaryPane unregisteredUsers={props.report.unregisteredUsers} />}
              </ColumnLayout>
            )}
          </div>
        </>
      ),
      footer: (
        <>
          <Button onClick={props.goToProject}>{t('Go to project')}</Button>
        </>
      ),
    };
  };

  const errorBody = (): IImportModalContent => {
    let errorMessage = t('there_was_something_wrong_with_the_json_file_please_try_again');

    if ((props.errorCodes?.length ?? 0) > 0) {
      if (props.errorCodes?.some((x) => x === 'IMPORT_DESERIALIZATION_FAILED')) {
        errorMessage = t('your_json_file_is_invalid_please_check_your_file_and_try_again');
      }
    }

    return {
      headerText: (
        <>
          {t('import_settings')} - <span className='fail'>{t('error')}</span>
        </>
      ),
      body: (
        <>
          {' '}
          <div>
            <p>
              {props.fileName ? (
                <span>
                  {t('selected_file')}: <b>{props.fileName}</b>
                </span>
              ) : (
                <span>{errorMessage}</span>
              )}
            </p>
            <S_Dropzone
              multiple={false}
              onDrop={props.filesDropped}
              accept={{ 'application/JSON': ['.json'] }}
            >
              <FontAwesomeIcon icon={faUpload} />
              {t('drop_your_json_file_here_or_select_it_manually')}
            </S_Dropzone>
          </div>
        </>
      ),
      footer: (
        <>
          <Button
            color='neutral'
            variant='outlined'
            onClick={() => {
              void props.modalHidden();
            }}
          >
            {t('cancel')}
          </Button>
          <Button
            disabled={!props.canUpload}
            onClick={props.filePicked}
          >
            {t('continue')}
          </Button>
        </>
      ),
    };
  };

  const bodyFactory = (): IImportModalContent => {
    switch (props.stage) {
      case ImportStage.PROCESSING:
        return processingBody();
      case ImportStage.ERROR:
        return errorBody();
      case ImportStage.FINISHED:
        return finishedBody();
      case ImportStage.STEP3:
        return stepThreeBody();
      case ImportStage.STEP2:
        return stepTwoBody();
      case ImportStage.STEP1:
      default:
        return stepOneBody();
    }
  };

  const handleClick = (event: SyntheticEvent): void => {
    event.preventDefault();
    props.modalsShown();
  };

  const content = bodyFactory();

  return (
    <EnableFor hasPermission={projectsPermissions.canImport()}>
      <Button
        variant='outlined'
        onClick={handleClick}
      >
        {t('import_from_a_file')}
      </Button>
      <ImportModalLayout
        isOpen={props.isShown}
        toggle={() => {
          void props.modalHidden();
        }}
        headerText={content.headerText}
        buttons={content.footer}
      >
        {!props.canCloseModal && <ImportModalOverlay />}
        {content.body}
      </ImportModalLayout>
    </EnableFor>
  );
});
